/**
 * Preview a unit as the platform actually renders it.
 *
 * This embeds Studio's chromeless container view, which renders every component through its
 * own XBlock view with its own CSS and JavaScript. That matters more than it sounds: a video
 * block's player, a problem's input widgets and a drag-and-drop's canvas are all real code
 * shipped by the block, including blocks from third parties we have never heard of. Any
 * preview we drew ourselves would be a drawing of a component, and would quietly diverge the
 * first time someone installed a new XBlock.
 *
 * Two things make it trustworthy rather than decorative:
 *
 *   - It renders the DRAFT, which is what the author just edited. The banner says so, because
 *     "preview" and "what learners currently see" are different questions when a unit has
 *     unpublished changes, and confusing them is how content ships early.
 *   - It follows its own content's height. This page DOES post `plugin.resize` (unlike the
 *     editor page), so the frame grows to fit instead of trapping the preview in a scrolling
 *     box inside a scrolling dialog.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Banner, Button, Dialog, Spinner } from '../../shared/ui';
import { isResizeMessage, startStudioSession, unitPreviewUrl } from '../../shared/lib/unit';
import './unit-preview.css';

/** Height bounds, so a bad message cannot collapse the frame or blow up the page. */
const MIN_FRAME = 320;
const MAX_FRAME = 6000;

/**
 * Hide Studio's authoring chrome inside the preview.
 *
 * The chromeless container is chromeless in the sense of having no page furniture, but each
 * component still renders its authoring header: a type label, a name, and edit / menu / drag
 * controls. Those controls talk to the authoring micro-frontend by postMessage, which is not
 * listening here — so in a preview they are buttons that look enabled and do nothing, sitting
 * on top of the content the author is trying to judge.
 *
 * Injected rather than requested because there is no parameter for it; the container view has
 * one mode. It is legitimate to reach in: the frame is same-origin by construction (that is
 * how the session cookie reaches it at all), so this is our own document.
 *
 * Selectors verified against the rendered container on release/verawood.1 — `.xblock-header`
 * carries the whole header including `.header-actions`, and `.wrapper-xblock-message` carries
 * Studio's own staff-only and validation notices, which are authoring information rather than
 * anything a learner sees.
 */
const PREVIEW_CSS = `
  .xblock-header,
  .wrapper-xblock-message,
  .ui-loading { display: none !important; }
  .wrapper-xblock,
  .studio-xblock-wrapper { border: 0 !important; box-shadow: none !important; margin: 0 0 1.25rem !important; }
  body { background: transparent !important; }
`;

export function UnitPreview({
  unitKey,
  unitName,
  hasChanges,
  onClose,
}: {
  unitKey: string;
  unitName: string;
  hasChanges: boolean;
  onClose: () => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  // Same ordering rule as the editor: the CMS session must exist BEFORE the iframe mounts.
  // Studio's container view is `@login_required`, and an iframe that loads anonymous is
  // redirected to a login page with nothing to reload it once the session arrives.
  const session = useQuery({
    queryKey: ['studio-session'],
    queryFn: startStudioSession,
    staleTime: 10 * 60_000,
  });

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // Same-origin only, and only from OUR frame: other embeds on the page can post
      // lookalike messages, and resizing this frame to their height would be wrong.
      if (event.origin !== window.location.origin) return;
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (!isResizeMessage(event.data)) return;

      const next = event.data.payload?.height;
      if (typeof next === 'number') setHeight(Math.min(Math.max(next, MIN_FRAME), MAX_FRAME));
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  /**
   * Strip the authoring chrome once the frame has a document.
   *
   * Runs on every load, not once: the container reloads itself after some operations, and a
   * style injected into the previous document goes with it.
   */
  function stripAuthoringChrome() {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    if (doc.getElementById('thinlms-preview-css')) return;
    const style = doc.createElement('style');
    style.id = 'thinlms-preview-css';
    style.textContent = PREVIEW_CSS;
    doc.head?.appendChild(style);
  }

  return (
    <Dialog
      title={`Preview: ${unitName}`}
      wide
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" onClick={onClose}>
            Close preview
          </Button>
          <span className="unit-preview__note">
            Rendered by the platform, exactly as a learner&rsquo;s browser will render it.
          </span>
        </>
      }
    >
      <div className="unit-preview">
        {hasChanges ? (
          <Banner tone="warning">
            <strong>This is the draft.</strong> It includes changes you have not published, so it
            is not what learners can see yet.
          </Banner>
        ) : (
          <div className="unit-preview__state">
            <Badge tone="success">Published</Badge>
            <span>This matches what learners see now.</span>
          </div>
        )}

        {session.isError ? (
          <Banner tone="danger">
            <strong>Could not load the preview.</strong> Signing in to the authoring service
            failed. Close this and reload the page; nothing has been changed.
          </Banner>
        ) : !session.data ? (
          <Spinner label="Preparing the preview…" />
        ) : (
          <iframe
            ref={frameRef}
            className="unit-preview__frame"
            style={height ? { height: `${height}px` } : undefined}
            src={unitPreviewUrl(unitKey)}
            title={`Preview of ${unitName}`}
            onLoad={stripAuthoringChrome}
          />
        )}
      </div>
    </Dialog>
  );
}
